import React, { useRef, useState } from 'react';
import { Camera } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BrandedSpinner } from '@/components/common/BrandedLoading';
import { ProfileAvatar } from '@/components/profiles/ProfileAvatar';
import {
  validateProfilePictureFile,
  uploadProfilePicture,
  getProfilePictureUploadErrorMessage,
  type UploadProfilePictureResult,
} from '@/services/profilePictureService';

const MAX_SIZE_MB = 5;
const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';

interface ProfilePictureUploadProps {
  userId: string;
  currentFileUrl: string | null | undefined;
  displayName: string;
  onSuccess: (result: UploadProfilePictureResult) => void;
  onError?: (message: string) => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  userId,
  currentFileUrl,
  displayName,
  onSuccess,
  onError,
  disabled = false,
  size = 'xl',
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleClick = () => {
    setError(null);
    setPreviewUrl(null);
    inputRef.current?.click();
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !userId) return;

    setError(null);
    setPreviewUrl(null);

    // Client-side validation with clear feedback
    try {
      validateProfilePictureFile(file);
    } catch (validationErr) {
      const msg = validationErr instanceof Error ? validationErr.message : 'Invalid file.';
      setError(msg);
      onError?.(msg);
      return;
    }

    // Optional: show preview (revoke when done to avoid leak)
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    setUploading(true);
    try {
      const result = await uploadProfilePicture(userId, file);
      onSuccess(result);
      setPreviewUrl(null);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      const msg = getProfilePictureUploadErrorMessage(err);
      setError(msg);
      onError?.(msg);
      setPreviewUrl(null);
      URL.revokeObjectURL(objectUrl);
    } finally {
      setUploading(false);
    }
  };

  const displaySrc = previewUrl ?? currentFileUrl ?? undefined;
  const isDisabled = disabled || uploading;

  return (
    <div className={cn('relative shrink-0', className)}>
      <button
        type="button"
        onClick={handleClick}
        disabled={isDisabled}
        className="relative rounded-full focus:outline-none focus:ring-4 focus:ring-primary/30 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
        aria-label={currentFileUrl ? 'Change profile photo' : 'Add profile photo'}
        aria-busy={uploading}
        aria-describedby={error ? 'profile-photo-error' : undefined}
      >
        <ProfileAvatar
          src={displaySrc}
          displayName={displayName}
          size={size}
          className="ring-4 ring-background shadow-xl"
        />
        <span
          className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 hover:opacity-100 focus-within:opacity-100 transition-opacity"
          aria-hidden
        >
          {uploading ? (
            <BrandedSpinner
              sizeClassName="h-10 w-10"
              className="drop-shadow-md [&_img]:brightness-0 [&_img]:invert"
            />
          ) : (
            <Camera className="h-10 w-10 text-white" aria-hidden />
          )}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        onChange={handleChange}
        aria-label="Choose profile picture file"
        tabIndex={-1}
      />
      {/* Hint and error */}
      <div className="mt-2 text-center min-h-[2rem]">
        <p className="text-xs text-muted-foreground" id="profile-photo-hint">
          JPEG, PNG, WebP or GIF. Max {MAX_SIZE_MB} MB.
        </p>
        {error && (
          <p
            id="profile-photo-error"
            className="text-xs text-destructive mt-1"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    </div>
  );
};
